import type { GSplatVisualShowcaseEffectOptions } from './GSplatVisualShowcaseEffect'
import { GSplatVisualShowcaseEffect } from './GSplatVisualShowcaseEffect'
import { SHOWCASE_SHADER_COMMON_GLSL, SHOWCASE_SHADER_COMMON_WGSL } from './showcaseShaderUtils'

const disintegrateShaderGLSL = /* glsl */ `
uniform float uTime;
uniform float uTimeScale;
uniform float uIntensity;
uniform vec3 uCenter;
uniform float uEffectScale;

${SHOWCASE_SHADER_COMMON_GLSL}

void modifySplatCenter(inout vec3 center) {
  vec3 local = (center - uCenter) / uEffectScale;
  float t = uTime * uTimeScale;
  float noise = showcaseHash13(floor(local * 11.0));
  float amount = smoothstep(0.0, 1.0, fract(t * 0.12 + noise));
  vec3 dir = normalize(vec3(
    showcaseHash12(local.xy) - 0.5,
    showcaseHash12(local.yz) - 0.5,
    abs(showcaseHash12(local.zx) - 0.2) + 0.2
  ));

  center += dir * amount * amount * 0.72 * uIntensity * uEffectScale;
}

void modifySplatCovariance(
  vec3 originalCenter,
  vec3 modifiedCenter,
  inout vec3 covA,
  inout vec3 covB
) {
  float t = uTime * uTimeScale;
  float noise = showcaseHash13(floor((originalCenter - uCenter) / uEffectScale * 11.0));
  float amount = smoothstep(0.0, 1.0, fract(t * 0.12 + noise));
  gsplatApplyUniformScale(covA, covB, max(0.18, 1.0 - amount * 0.58 * uIntensity));
}

void modifySplatColor(vec3 center, inout vec4 color) {
  vec3 local = (center - uCenter) / uEffectScale;
  float t = uTime * uTimeScale;
  float noise = showcaseHash13(floor(local * 11.0));
  float amount = smoothstep(0.0, 1.0, fract(t * 0.12 + noise));
  vec3 dust = mix(vec3(0.22, 0.16, 0.12), vec3(1.0, 0.64, 0.24), amount);
  color.rgb = mix(color.rgb, dust, showcaseSaturate(amount * 0.72 * uIntensity));
  color.a *= 1.0 - amount * 0.42 * showcaseSaturate(uIntensity);
}
`

const disintegrateShaderWGSL = /* wgsl */ `
${SHOWCASE_SHADER_COMMON_WGSL}

fn modifySplatCenter(center: ptr<function, vec3f>) {
  let local = (*center - uniform.uCenter) / uniform.uEffectScale;
  let t = uniform.uTime * uniform.uTimeScale;
  let noise = showcaseHash13(floor(local * 11.0));
  let amount = smoothstep(0.0, 1.0, fract(t * 0.12 + noise));
  let direction = normalize(vec3f(
    showcaseHash12(local.xy) - 0.5,
    showcaseHash12(local.yz) - 0.5,
    abs(showcaseHash12(local.zx) - 0.2) + 0.2
  ));
  *center = *center + direction * amount * amount * 0.72 *
    uniform.uIntensity * uniform.uEffectScale;
}

fn modifySplatRotationScale(
  originalCenter: vec3f,
  modifiedCenter: vec3f,
  rotation: ptr<function, vec4f>,
  scale: ptr<function, vec3f>
) {
  let t = uniform.uTime * uniform.uTimeScale;
  let noise = showcaseHash13(floor((originalCenter - uniform.uCenter) /
    uniform.uEffectScale * 11.0));
  let amount = smoothstep(0.0, 1.0, fract(t * 0.12 + noise));
  *scale = (*scale) * max(0.18, 1.0 - amount * 0.58 * uniform.uIntensity);
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
  let local = (center - uniform.uCenter) / uniform.uEffectScale;
  let t = uniform.uTime * uniform.uTimeScale;
  let noise = showcaseHash13(floor(local * 11.0));
  let amount = smoothstep(0.0, 1.0, fract(t * 0.12 + noise));
  let dust = mix(vec3f(0.22, 0.16, 0.12), vec3f(1.0, 0.64, 0.24), amount);
  (*color) = vec4f(
    mix((*color).rgb, dust, showcaseSaturate(amount * 0.72 * uniform.uIntensity)),
    (*color).a * (1.0 - amount * 0.42 * showcaseSaturate(uniform.uIntensity))
  );
}
`

export class GSplatVisualDisintegrateEffect extends GSplatVisualShowcaseEffect {
  constructor(options: GSplatVisualShowcaseEffectOptions = {}) {
    super(
      {
        id: 'visual-disintegrate',
        shader: disintegrateShaderGLSL,
        shaderWGSL: disintegrateShaderWGSL,
      },
      options
    )
  }
}
